import { z } from 'zod';
import { InteractionProvider } from '../index.js';
import { InteractionEventType } from './index.js';
// --- Shared Sub-schemas ---
const MailgunBaseEventSchema = z.object({
    provider: z.literal(InteractionProvider.Mailgun),
});
const SignatureSchema = z.object({
    token: z.string(),
    signature: z.string(),
    timestamp: z.string(),
});
const UserVariablesSchema = z.object({
    tenantId: z.string().optional(),
    interactionId: z.string().optional(),
});
const MessageHeadersSchema = z.object({
    to: z.string().optional(),
    from: z.string().optional(),
    subject: z.string().nullable().optional(),
    'message-id': z.string().optional(),
});
const MessageSchema = z.object({
    size: z.number().optional(),
    headers: MessageHeadersSchema,
    attachments: z.array(z.unknown()).optional(),
});
const StorageSchema = z.object({
    key: z.string(),
    url: z.string(),
});
const EnvelopeSchema = z.object({
    sender: z.string().optional(),
    targets: z.string().optional(),
    'i-ip-band': z.string().optional(),
    transport: z.string().optional(),
    'sending-ip': z.string().optional(),
    'i-ip-is-dedicated': z.boolean().optional(),
});
const FlagsSchema = z.object({
    'is-big': z.boolean().optional(),
    'is-routed': z.boolean().optional(),
    'is-test-mode': z.boolean().optional(),
    'is-system-test': z.boolean().optional(),
    'is-authenticated': z.boolean().optional(),
    'is-delayed-bounce': z.boolean().optional(),
});
const DeliveryStatusSchema = z.object({
    tls: z.boolean().optional(),
    code: z.number().or(z.string()).optional(),
    utf8: z.boolean().optional(),
    message: z.string().optional(),
    'mx-host': z.string().optional(),
    'attempt-no': z.number().optional(),
    description: z.string().optional(),
    'enhanced-code': z.string().optional(),
    'session-seconds': z.number().optional(),
    'certificate-verified': z.boolean().optional(),
    'first-delivery-attempt-seconds': z.number().optional(),
    'retry-seconds': z.number().optional(),
    'last-code': z.number().optional(),
    'last-message': z.string().optional(),
    'bounce-type': z.string().optional(),
    'bounce-code': z.string().optional(),
});
const ClientInfoSchema = z.object({
    bot: z.string().optional(),
    'client-os': z.string(),
    'user-agent': z.string(),
    'client-name': z.string(),
    'client-type': z.string(),
    'device-type': z.string(),
});
const GeolocationSchema = z.object({
    city: z.string(),
    region: z.string(),
    country: z.string(),
    timezone: z.string().optional(),
});
// --- Base Event Data ---
const BaseEventData = z.object({
    id: z.string(),
    tags: z.array(z.string()),
    flags: FlagsSchema,
    message: MessageSchema,
    storage: StorageSchema.optional(),
    envelope: EnvelopeSchema.optional(),
    campaigns: z.array(z.unknown()),
    'log-level': z.string().optional(),
    recipient: z.string(),
    timestamp: z.number(),
    'primary-dkim': z.string().optional(),
    'user-variables': UserVariablesSchema,
    'recipient-domain': z.string().optional(),
    'recipient-provider': z.string().optional(),
});
// Failed Event
const MailgunFailedEventBronzeSchema = z.object({
    signature: SignatureSchema,
    'event-data': BaseEventData.extend({
        event: z.literal('failed'),
        reason: z.string(),
        severity: z.string(),
        'delivery-status': DeliveryStatusSchema,
    }),
});
export const MailgunFailedEventSchema = MailgunBaseEventSchema.extend({
    bronzeData: MailgunFailedEventBronzeSchema,
});
// Delivered Event
const MailgunDeliveredEventBronzeSchema = z.object({
    signature: SignatureSchema,
    'event-data': BaseEventData.extend({
        event: z.literal('delivered'),
        'delivery-status': DeliveryStatusSchema,
    }),
});
export const MailgunDeliveredEventSchema = MailgunBaseEventSchema.extend({
    bronzeData: MailgunDeliveredEventBronzeSchema,
});
// Opened Event
const MailgunOpenedEventBronzeSchema = z.object({
    signature: SignatureSchema,
    'event-data': z.object({
        id: z.string(),
        ip: z.string().optional(),
        tags: z.array(z.string()),
        event: z.literal('opened'),
        message: z.object({
            headers: z.object({
                'message-id': z.string(),
            }),
        }),
        campaigns: z.array(z.unknown()),
        'log-level': z.string(),
        recipient: z.string(),
        timestamp: z.number(),
        'client-info': ClientInfoSchema.optional(),
        geolocation: GeolocationSchema.optional(),
        'user-variables': UserVariablesSchema,
        'recipient-domain': z.string(),
        'recipient-provider': z.string().optional(),
    }),
});
export const MailgunOpenedEventSchema = MailgunBaseEventSchema.extend({
    bronzeData: MailgunOpenedEventBronzeSchema,
});
// Clicked Event
const MailgunClickedEventBronzeSchema = z.object({
    signature: SignatureSchema,
    'event-data': z.object({
        id: z.string(),
        ip: z.string().optional(),
        tags: z.array(z.string()),
        event: z.literal('clicked'),
        message: z.object({
            headers: z.object({
                'message-id': z.string(),
            }),
        }),
        campaigns: z.array(z.unknown()),
        'log-level': z.string(),
        recipient: z.string(),
        timestamp: z.number(),
        'client-info': ClientInfoSchema.optional(),
        geolocation: GeolocationSchema.optional(),
        'user-variables': UserVariablesSchema,
        'recipient-domain': z.string(),
        'recipient-provider': z.string().optional(),
    }),
});
export const MailgunClickedEventSchema = MailgunBaseEventSchema.extend({
    bronzeData: MailgunClickedEventBronzeSchema,
});
// Union type for all Mailgun events
const MailgunBronzeEventSchema = z.union([
    MailgunDeliveredEventBronzeSchema,
    MailgunFailedEventBronzeSchema,
    MailgunOpenedEventBronzeSchema,
]);
export const mapMailgunEvent = (bronzeEvent) => {
    switch (bronzeEvent['event-data'].event) {
        case 'delivered': {
            const data = bronzeEvent['event-data'];
            return {
                provider: InteractionProvider.Mailgun,
                type: InteractionEventType.Delivered,
                bronzeData: bronzeEvent,
                data: {
                    recipientDomain: data['recipient-domain'],
                    recipientEmail: data.recipient,
                    mailboxProvider: data['recipient-provider'],
                    recipientIpAddress: undefined,
                    deliveryMethod: data?.envelope?.transport,
                    routingDomain: data['recipient-domain'],
                },
            };
        }
        case 'failed': {
            const failedEvent = bronzeEvent;
            const data = failedEvent['event-data'];
            const severity = data.severity;
            const deliveryStatus = data['delivery-status'];
            // If severity is "temporary", map to Delayed, otherwise Failed
            if (severity === 'temporary') {
                return {
                    provider: InteractionProvider.Mailgun,
                    bronzeData: failedEvent,
                    type: InteractionEventType.Delayed,
                    data: {
                        recipientEmail: data.recipient,
                        bounceClass: deliveryStatus?.['bounce-type'],
                        errorCode: deliveryStatus?.code?.toString(),
                        reason: data.reason,
                        queueTime: undefined,
                        numRetries: deliveryStatus?.['attempt-no']?.toString(),
                    },
                };
            }
            else {
                return {
                    provider: InteractionProvider.Mailgun,
                    bronzeData: failedEvent,
                    type: InteractionEventType.Failed,
                    data: {
                        recipientEmail: data.recipient,
                        bounceClass: deliveryStatus?.['bounce-type'],
                        errorCode: deliveryStatus?.code.toString(),
                        reason: data.reason,
                    },
                };
            }
        }
        case 'opened': {
            const openedEvent = bronzeEvent;
            const data = openedEvent['event-data'];
            return {
                provider: InteractionProvider.Mailgun,
                bronzeData: openedEvent,
                type: InteractionEventType.Opened,
                data: {
                    geoIp: data.geolocation
                        ? {
                            zipcode: undefined,
                            city: data.geolocation.city,
                            country: data.geolocation.country,
                            longitude: undefined,
                            latitude: undefined,
                        }
                        : undefined,
                },
            };
        }
        case 'clicked': {
            const clickedEvent = bronzeEvent;
            const data = clickedEvent['event-data'];
            return {
                provider: InteractionProvider.Mailgun,
                bronzeData: clickedEvent,
                type: InteractionEventType.Clicked,
                data: {
                    geoIp: data.geolocation
                        ? {
                            zipcode: undefined,
                            city: data.geolocation.city,
                            country: data.geolocation.country,
                            longitude: undefined,
                            latitude: undefined,
                        }
                        : undefined,
                },
            };
        }
        default:
            return null;
    }
};
//# sourceMappingURL=mailgun.js.map